import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageTitle } from '../styles/page';
import { Input, Label, ErrorMessage } from '../styles/signup';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';

const PageWrapper = styled(motion.section)`
  padding: 10rem 5rem 8rem;
  min-height: 60vh;
  background: #f0efea;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 10rem 1.5rem 4rem;
  }
`;

const AuthCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border: 1px solid #dcdad3;
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  margin: 0.5rem 0 2rem;
  line-height: 1.6;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
`;

const FieldGroup = styled.div`
  text-align: left;
`;

const SubmitButton = styled.button`
  padding: 1rem;
  border: none;
  border-radius: 8px;
  background: #d1ff94;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/perks';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError('Incorrect email or password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <AuthCard>
        <PageTitle style={{ marginBottom: 0 }}>Sign In</PageTitle>
        <Subtitle>Access your Playfair account.</Subtitle>

        <StyledForm onSubmit={handleSubmit}>
          <FieldGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              error={!!error}
            />
          </FieldGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign In'}
          </SubmitButton>
        </StyledForm>
      </AuthCard>
    </PageWrapper>
  );
}
